import { ApplicationNextStep } from '../../components/applications/ApplicationNextStep';
import { normalizeApplicationStatus, applicationStatusLabel } from '../../../../shared/src/utils/applicationStatus';
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { Plus, Search, Filter, FileText, Eye, Edit, Trash2, CheckCircle, XCircle, Clock, ArrowRight, UserCog, Users, Save } from 'lucide-react';
import { agentApi } from '@/services/agentApi';
import { AgentStudentApplication } from '@/types/user';
import { parseApplicationFilters, updateApplicationFilters } from '@/utils/applicationListFilters';
import { useAuth } from '@/hooks/useAuth';

export const ApplicationsListPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [applications, setApplications] = useState<AgentStudentApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = parseApplicationFilters(searchParams);
  const { status: statusFilter, group, page: currentPage } = filters;
  const [searchTerm, setSearchTerm] = useState(filters.search);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const requestVersion = useRef(0);
  const changeFilters = (changes: Record<string, string>) => setSearchParams(updateApplicationFilters(searchParams, changes));
  const setCurrentPage = (page: number) => setSearchParams(updateApplicationFilters(searchParams, { page: String(page) }));
  useEffect(() => { setSearchTerm(filters.search); }, [filters.search]);
  const [totalPages, setTotalPages] = useState(1);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<AgentStudentApplication | null>(null);
  const [subAccounts, setSubAccounts] = useState<any[]>([]);
  const [loadingSubAccounts, setLoadingSubAccounts] = useState(false);

  const isMainAgent = user?.agentProfile && !user.agentProfile.parentAgentId;

  useEffect(() => {
    if (!user?.agentProfile) return;
    loadApplications();
    return () => { requestVersion.current += 1; };
  }, [searchParams.toString(), user?.agentProfile?.id]);

  useEffect(() => { if (isMainAgent) loadSubAccounts(); }, [isMainAgent]);

  const loadSubAccounts = async () => {
    try {
      setLoadingSubAccounts(true);
      const accounts = await agentApi.getSubAccounts();
      setSubAccounts(accounts);
    } catch (error) {
      console.error('Error loading sub-accounts:', error);
    } finally {
      setLoadingSubAccounts(false);
    }
  };

  const loadApplications = async () => {
    const version = ++requestVersion.current;
    setError('');
    if (filters.error) { setError(filters.error); setLoading(false); return; }
    setLoading(true);
    try {
      const result = await agentApi.getApplications({
        page: currentPage, limit: 10, status: statusFilter || undefined,
        group: group || undefined, search: filters.search || undefined,
        includeSubAccounts: Boolean(isMainAgent),
      });
      if (version !== requestVersion.current) return;
      setApplications(result.applications);
      setTotalPages(result.pages);
      setTotal(result.total);
    } catch (error) {
      if (version === requestVersion.current) setError('Failed to load applications. Please retry.');
    } finally {
      if (version === requestVersion.current) setLoading(false);
    }
  };

  const handleSearch = () => changeFilters({ search: searchTerm.trim() });

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { class: string; icon: React.ReactNode; label: string }> = {
      DRAFT: { class: 'bg-secondary', icon: <FileText size={14} />, label: 'Draft' },
      SUBMITTED: { class: 'bg-info', icon: <Clock size={14} />, label: 'Submitted' },
      APPROVED: { class: 'bg-success', icon: <CheckCircle size={14} />, label: 'Approved' },
      VISA_APPLICATION: { class: 'bg-info', icon: <FileText size={14} />, label: 'Visa Application' },
      TUITION_PAYMENT_STAGE: { class: 'bg-info', icon: <FileText size={14} />, label: 'Tuition Payment' },
      PAID: { class: 'bg-success', icon: <CheckCircle size={14} />, label: 'Paid' },
      VISA_APPROVED: { class: 'bg-success', icon: <CheckCircle size={14} />, label: 'Visa Approved' },
      REJECTED: { class: 'bg-danger', icon: <XCircle size={14} />, label: 'Rejected' },
      DEFERRED: { class: 'bg-info', icon: <Clock size={14} />, label: 'Deferred' },
      WITHDRAWN: { class: 'bg-secondary', icon: <XCircle size={14} />, label: 'Withdrawn' },
      ENROLLED: { class: 'bg-success', icon: <CheckCircle size={14} />, label: 'Enrolled' },
    };

    const badge = badges[normalizeApplicationStatus(status)] || { class: 'bg-secondary', icon: badges.DRAFT.icon };
    return (
      <span className={`badge ${badge.class} d-flex align-items-center gap-1`}>
        {badge.icon}
        {applicationStatusLabel(status)}
      </span>
    );
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this application?')) {
      try {
        await agentApi.deleteApplication(id);
        loadApplications();
        toast.success('Application deleted successfully');
      } catch (error) {
        console.error('Error deleting application:', error);
        toast.error('Failed to delete application');
      }
    }
  };

  const handleTransfer = async (application: AgentStudentApplication) => {
    if (!window.confirm(`Are you sure you want to take over this application from ${application.agent_profiles?.agencyName || 'sub-account'}?`)) {
      return;
    }

    try {
      await agentApi.transferApplication(application.id);
      toast.success('Application transferred successfully');
      loadApplications();
    } catch (error: any) {
      console.error('Error transferring application:', error);
      toast.error(error.response?.data?.error || 'Failed to transfer application');
    }
  };

  const handleReassign = async (subAccountId: number) => {
    if (!selectedApplication) return;

    try {
      await agentApi.reassignApplication(selectedApplication.id, subAccountId);
      toast.success('Application reassigned successfully');
      setShowReassignModal(false);
      setSelectedApplication(null);
      loadApplications();
    } catch (error: any) {
      console.error('Error reassigning application:', error);
      toast.error(error.response?.data?.error || 'Failed to reassign application');
    }
  };

  const handleSaveAsProfile = async (application: AgentStudentApplication, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!window.confirm('Would you like to save this application data as a student profile for future use?')) {
      return;
    }

    try {
      await agentApi.createStudentProfile({
        applicationId: application.id,
      });
      toast.success('Student profile created successfully!');
    } catch (error: any) {
      console.error('Error saving profile:', error);
      toast.error(error?.response?.data?.error || 'Failed to save profile');
    }
  };

  const openReassignModal = (application: AgentStudentApplication) => {
    setSelectedApplication(application);
    setShowReassignModal(true);
  };

  const getApplicationOwner = (application: AgentStudentApplication) => {
    if (!application.agent_profiles) return null;
    const agentProfile = application.agent_profiles as any;
    if (agentProfile.parentAgentId && agentProfile.users) {
      const user = agentProfile.users as any;
      return {
        name: `${user.firstName} ${user.lastName}`,
        agencyName: agentProfile.agencyName,
      };
    }
    return null;
  };

  const isOwnedBySubAccount = (application: AgentStudentApplication) => {
    if (!isMainAgent) return false;
    if (!application.agent_profiles) return false;
    const agentProfile = application.agent_profiles as any;
    return agentProfile.parentAgentId !== null && agentProfile.parentAgentId !== undefined;
  };

  return (
    <div className="container-fluid py-4">
      <div className="row mb-4">
        <div className="col">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h1 className="h3 fw-bold text-dark mb-1">Applications</h1>
              <p className="text-muted mb-0">Manage student applications</p>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => navigate('/applications/new')}
            >
              <Plus size={18} className="me-2" />
              New Application
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <Search size={18} className="text-muted" />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name, email, or reference..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <button
                  className="btn btn-primary"
                  onClick={handleSearch}
                >
                  Search
                </button>
              </div>
            </div>
            <div className="col-md-3">
              <div className="input-group">
                <span className="input-group-text bg-light">
                  <Filter size={18} className="text-muted" />
                </span>
                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => {
                    changeFilters({ status: e.target.value, group: '' });
                  }}
                >
                  <option value="">{group === 'pending' ? 'Submitted applications' : group === 'approved' ? 'Approved applications' : 'All Statuses'}</option>
                  <option value="DRAFT">Draft</option>
                  <option value="SUBMITTED">Submitted</option>
                  <option value="APPROVED">Approved</option>
                  <option value="TUITION_PAYMENT_STAGE">Tuition Payment</option>
                  <option value="VISA_APPLICATION">Visa Application</option>
                  <option value="PAID">Paid</option>
                  <option value="VISA_APPROVED">Visa Approved</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="DEFERRED">Deferred</option>
                  <option value="WITHDRAWN">Withdrawn</option>
                  <option value="ENROLLED">Enrolled</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3" aria-live="polite">
        <span>{group === 'pending' ? 'Submitted applications' : group === 'approved' ? 'Approved' : statusFilter ? applicationStatusLabel(statusFilter) : 'All applications'}</span>
        <span className="text-muted">— {isMainAgent ? 'Agency and sub-accounts' : 'Own applications'}</span>
        {(group || statusFilter) && <button className="btn btn-sm btn-outline-secondary" onClick={() => changeFilters({ group: '', status: '' })}>Clear filter</button>}
        {(searchParams.toString() || searchTerm) && <button className="btn btn-sm btn-outline-secondary" onClick={() => { setSearchTerm(''); setSearchParams({}); }}>Reset</button>}
        {!loading && !error && <span className="ms-auto text-muted">{total} {total === 1 ? 'application' : 'applications'}</span>}
      </div>
      {/* Applications Table */}
      <div className="card border-0 shadow-sm">
        <div className="card-body p-0">
          {error ? (
            <div className="alert alert-danger m-3" role="alert">{error} {!filters.error && <button className="btn btn-sm btn-outline-danger ms-2" onClick={loadApplications}>Retry</button>}</div>
          ) : loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : applications.length === 0 ? (
            <div className="text-center py-5">
              <FileText size={48} className="text-muted mb-3" />
              <p className="text-muted">No applications found</p>
              <button
                className="btn btn-primary"
                onClick={() => navigate('/applications/new')}
              >
                <Plus size={18} className="me-2" />
                Create Application
              </button>
            </div>
          ) : (
              <>
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Reference</th>
                      <th>Student Name</th>
                      <th>Email</th>
                      <th>University</th>
                      <th>Program</th>
                      {isMainAgent && <th>Owner</th>}
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications.map((app) => (
                      <motion.tr
                        key={app.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="cursor-pointer"
                        onClick={() => navigate(`/applications/${app.id}`)}
                      >
                        <td>
                          <Link to={`/applications/${app.id}`} onClick={e => e.stopPropagation()}><code>{app.reference}</code></Link>
                        </td>
                        <td>
                          <Link to={`/applications/${app.id}`} onClick={e => e.stopPropagation()}>{app.firstName} {app.lastName}</Link>
                        </td>
                        <td>{app.email}</td>
                        <td>{app.universities?.name || 'N/A'}</td>
                        <td>{app.university_programs?.programs?.name || 'N/A'}</td>
                        {isMainAgent && (
                          <td>
                            {isOwnedBySubAccount(app) ? (
                              <span className="badge bg-light text-body border border-info">
                                {getApplicationOwner(app)?.name || 'Sub-Account'}
                              </span>
                            ) : (
                              <span className="badge bg-light text-body border border-primary">You</span>
                            )}
                          </td>
                        )}
                        <td>{getStatusBadge(app.status)}<ApplicationNextStep status={app.status} /></td>
                        <td>{new Date(app.createdAt).toLocaleDateString()}</td>
                        <td>
                          <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
                            {app.status === 'DRAFT' ? (
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => navigate(`/applications/${app.id}/edit`)}
                                title="Continue Editing"
                              >
                                <Edit size={16} />
                              </button>
                            ) : (
                              <>
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => navigate(`/applications/${app.id}`)}
                                  title="View"
                                >
                                  <Eye size={16} />
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={(e) => handleSaveAsProfile(app, e)}
                                  title="Save as Profile"
                                >
                                  <Save size={16} />
                                </button>
                              </>
                            )}
                            {isMainAgent && (
                              <>
                                {isOwnedBySubAccount(app) && (
                                  <button
                                    className="btn btn-sm btn-outline-success"
                                    onClick={() => handleTransfer(app)}
                                    title="Take Over"
                                  >
                                    <ArrowRight size={16} />
                                  </button>
                                )}
                                <button
                                  className="btn btn-sm btn-outline-info"
                                  onClick={() => openReassignModal(app)}
                                  title="Reassign"
                                >
                                  <UserCog size={16} />
                                </button>
                              </>
                            )}
                            {app.status === 'DRAFT' && (
                              <>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDelete(app.id)}
                                  title="Delete"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="card-footer bg-white">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <small className="text-muted">
                        Page {currentPage} of {totalPages}
                      </small>
                    </div>
                    <div className="btn-group">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Reassign Modal */}
      {showReassignModal && selectedApplication && (
        <motion.div
          className="modal fade show d-block"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1050,
            backdropFilter: 'blur(4px)',
          }}
          onClick={() => {
            setShowReassignModal(false);
            setSelectedApplication(null);
          }}
          tabIndex={-1}
        >
          <motion.div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            style={{ zIndex: 1051, maxWidth: '600px' }}
          >
            <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '12px', overflow: 'hidden' }}>
              <div className="modal-header border-bottom bg-light" style={{ padding: '1.25rem 1.5rem' }}>
                <div className="d-flex align-items-center">
                  <div
                    className="d-flex align-items-center justify-content-center me-3"
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      backgroundColor: 'rgba(30, 64, 175, 0.1)',
                    }}
                  >
                    <UserCog size={20} className="text-primary" />
                  </div>
                  <div>
                    <h5 className="modal-title fw-bold mb-0">Reassign Application</h5>
                    <small className="text-muted">Select a sub-account to reassign to</small>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowReassignModal(false);
                    setSelectedApplication(null);
                  }}
                  aria-label="Close"
                />
              </div>
              <div className="modal-body" style={{ padding: '1.5rem' }}>
                <div className="mb-4 p-3 bg-light rounded" style={{ borderRadius: '8px' }}>
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <div
                        className="d-flex align-items-center justify-content-center"
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '12px',
                          backgroundColor: 'rgba(30, 64, 175, 0.1)',
                        }}
                      >
                        <FileText size={24} className="text-primary" />
                      </div>
                    </div>
                    <div className="flex-grow-1">
                      <div className="fw-semibold text-dark mb-1">Application Reference</div>
                      <code className="text-primary fw-bold" style={{ fontSize: '1.1rem' }}>
                        {selectedApplication.reference}
                      </code>
                    </div>
                  </div>
                </div>

                {loadingSubAccounts ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted mt-3 mb-0">Loading sub-accounts...</p>
                  </div>
                ) : subAccounts.filter(sa => {
                  const currentOwnerId = (selectedApplication.agent_profiles as any)?.id;
                  return sa.id !== currentOwnerId;
                }).length === 0 ? (
                  <div className="text-center py-5">
                    <div
                      className="d-inline-flex align-items-center justify-content-center mb-3"
                      style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '16px',
                        backgroundColor: 'rgba(100, 116, 139, 0.1)',
                      }}
                    >
                      <Users size={32} className="text-muted" />
                    </div>
                    <p className="text-muted mb-0">No sub-accounts available for reassignment</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-muted mb-3 fw-semibold">Select a sub-account:</p>
                    <div className="d-flex flex-column gap-2" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {subAccounts
                        .filter(sa => {
                          const currentOwnerId = (selectedApplication.agent_profiles as any)?.id;
                          return sa.id !== currentOwnerId;
                        })
                        .map((subAccount) => (
                          <motion.button
                            key={subAccount.id}
                            type="button"
                            className="btn btn-outline-primary text-start p-3 border-2"
                            onClick={() => handleReassign(subAccount.id)}
                            style={{
                              borderRadius: '12px',
                              borderColor: 'rgba(30, 64, 175, 0.2)',
                              boxShadow: 'none',
                              position: 'relative',
                            }}
                            whileHover={{
                              borderColor: 'rgba(30, 64, 175, 0.5)',
                              boxShadow: '0 4px 12px rgba(30, 64, 175, 0.15)',
                              backgroundColor: 'rgba(30, 64, 175, 0.02)',
                            }}
                            whileTap={{ scale: 0.99 }}
                            transition={{
                              duration: 0.2,
                              ease: 'easeOut',
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="d-flex align-items-center flex-grow-1">
                                <div
                                  className="d-flex align-items-center justify-content-center me-3"
                                  style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '12px',
                                    backgroundColor: 'rgba(30, 64, 175, 0.1)',
                                    flexShrink: 0,
                                  }}
                                >
                                  <Users size={20} className="text-primary" />
                                </div>
                                <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                  <div className="fw-semibold text-dark mb-1" style={{ fontSize: '1rem' }}>
                                    {subAccount.firstName} {subAccount.lastName}
                                  </div>
                                  <div className="text-muted small mb-1">
                                    <span className="d-inline-flex align-items-center">
                                      {subAccount.email}
                                    </span>
                                  </div>
                                  <div className="text-muted small">
                                    <span className="badge bg-light text-dark border">
                                      {subAccount.agencyName}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="ms-3 flex-shrink-0">
                                <div
                                  className="d-flex align-items-center justify-content-center"
                                  style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '8px',
                                    backgroundColor: 'rgba(30, 64, 175, 0.1)',
                                  }}
                                >
                                  <ArrowRight size={18} className="text-primary" />
                                </div>
                              </div>
                            </div>
                          </motion.button>
                        ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer border-top bg-light" style={{ padding: '1rem 1.5rem' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowReassignModal(false);
                    setSelectedApplication(null);
                  }}
                  style={{ borderRadius: '8px', padding: '0.5rem 1.5rem' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};
